import type { NodeKey } from 'lexical';
import { createContext, FC, memo, useContext } from 'react';

import type { NoteEditorBlockDef } from '../../../config';

import type { TemplateInstructionRole } from './templateNodeState';

export type TemplateFieldWidget = FC<{
  nodeKey: NodeKey;
  def: NoteEditorBlockDef;
  // An instruction's: a slot or its section's guidance.
  role?: TemplateInstructionRole;
}>;

// What a field and an AI instruction of the template render as. The layer
// that owns them hands them in, so the editor never imports its widgets.
export type TemplateFieldWidgets = {
  field: TemplateFieldWidget;
  instruction: TemplateFieldWidget;
};

export const TemplateFieldWidgetsContext =
  createContext<TemplateFieldWidgets | null>(null);

// Lexical calls `decorate` only for a node that changed, so a field renders
// again only when its own definition does.
export const TemplateFieldHost: FC<{
  kind: keyof TemplateFieldWidgets;
  nodeKey: NodeKey;
  def: NoteEditorBlockDef;
  role?: TemplateInstructionRole;
}> = memo(({ kind, nodeKey, def, role }) => {
  const Widget = useContext(TemplateFieldWidgetsContext)?.[kind];
  return Widget ? <Widget nodeKey={nodeKey} def={def} role={role} /> : null;
});

TemplateFieldHost.displayName = 'TemplateFieldHost';
